import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getNews, getNewsMeta } from '../api';
import { useIsMobile } from '../hooks/useIsMobile';

// Crypto news from the news-tracking job (crypto_tracker.news, refreshed
// every 6h). Every article is stored in both languages, so the UI language
// picks title/summary/analysis _vi or _en.
const isVi = (i18n) => (i18n.language || 'vi').startsWith('vi');
const pick = (doc, field, i18n) => (isVi(i18n) ? doc[`${field}_vi`] || doc[`${field}_en`] : doc[`${field}_en`] || doc[`${field}_vi`]);

const SENTIMENT_COLOR = { bullish: '#10b981', bearish: '#ef4444', neutral: '#64748b' };
const IMPACT_COLOR = { high: '#ef4444', medium: '#f59e0b', low: '#64748b' };
const PAGE_SIZE = 20;

function fmtTime(iso, i18n) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString(isVi(i18n) ? 'vi-VN' : 'en-US', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const SENTIMENT_ICON = { bullish: '▲', bearish: '▼', neutral: '●' };

// Compact tile: sentiment strip + label, title, affected-token tags, and a
// 2-line summary. Clicking the tile expands the full summary and analysis.
function NewsCard({ doc, onToken }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const analysis = pick(doc, 'analysis', i18n);
  const color = SENTIMENT_COLOR[doc.sentiment] || SENTIMENT_COLOR.neutral;
  const tokens = doc.impacted_tokens || [];
  return (
    <article style={{ ...s.card, borderLeft: `4px solid ${color}` }} onClick={() => setOpen((v) => !v)}>
      <div style={s.cardMeta}>
        {doc.sentiment && (
          <span style={{ ...s.sentiment, color, background: `${color}1f` }}>
            {SENTIMENT_ICON[doc.sentiment] || ''} {t(`news.sentiment.${doc.sentiment}`, doc.sentiment)}
          </span>
        )}
        {doc.impact_level && (
          <span style={{ ...s.impact, color: IMPACT_COLOR[doc.impact_level] }} title={t('news.impact', { level: t(`news.impactLevel.${doc.impact_level}`) })}>
            {t('news.impact', { level: t(`news.impactLevel.${doc.impact_level}`, doc.impact_level) })}{doc.impact_score != null ? ` ${doc.impact_score}/5` : ''}
          </span>
        )}
        <span style={s.time}>{fmtTime(doc.published_at, i18n)}</span>
      </div>
      <h3 style={s.cardTitle}>{pick(doc, 'title', i18n)}</h3>
      {tokens.length > 0 && (
        <div style={s.tokens}>
          {tokens.map((tk) => (
            <button
              key={tk} style={{ ...s.token, color, borderColor: `${color}80` }} title={t('news.filterToken', { token: tk })}
              onClick={(e) => { e.stopPropagation(); onToken(tk); }}
            >
              {SENTIMENT_ICON[doc.sentiment] || ''} {tk}
            </button>
          ))}
        </div>
      )}
      <p style={open ? s.summary : { ...s.summary, ...s.clamp }}>{pick(doc, 'summary', i18n)}</p>
      {open && analysis && <div style={s.analysis}>{analysis}</div>}
      {open && (
        <div style={s.cardFoot}>
          {(doc.categories || []).map((c) => <span key={c} style={s.tag}>{c}</span>)}
          {(doc.sources || []).filter((src) => /^https?:\/\//.test(src.url || '')).map((src) => (
            <a key={src.url} href={src.url} target="_blank" rel="noopener noreferrer" style={s.sourceLink} onClick={(e) => e.stopPropagation()}>
              {src.outlet || new URL(src.url).hostname} ↗
            </a>
          ))}
        </div>
      )}
      {!open && analysis && <span style={s.more}>{t('news.showAnalysis')}</span>}
    </article>
  );
}

export default function NewsPage() {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const [meta, setMeta] = useState({ categories: [], tokens: [], updatedAt: null });
  const [filters, setFilters] = useState({ category: '', sentiment: '', impact: '', token: '', q: '' });
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ total: 0, docs: [] });
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => { getNewsMeta().then(setMeta).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setReady(false); setError(false);
    try { setData(await getNews({ ...filters, page, limit: PAGE_SIZE })); }
    catch { setError(true); setData({ total: 0, docs: [] }); }
    finally { setReady(true); }
  }, [filters, page]);
  useEffect(() => { load(); }, [load]);

  const setFilter = (k, v) => { setPage(1); setFilters((f) => ({ ...f, [k]: v })); };
  const pages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  return (
    <div style={isMobile ? { ...s.page, padding: '16px 12px' } : s.page}>
      <div style={s.header}>
        <h2 style={s.title}>{t('news.title')}</h2>
        <p style={s.subtitle}>
          {t('news.subtitle')}
          {meta.updatedAt && ` ${t('news.updated', { time: fmtTime(meta.updatedAt, i18n) })}`}
        </p>
      </div>

      <div style={s.filters}>
        <form style={s.searchForm} onSubmit={(e) => { e.preventDefault(); setFilter('q', query.trim()); }}>
          <input style={s.input} value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('news.searchPlaceholder')} />
          <button type="submit" style={s.searchBtn}>{t('news.search')}</button>
        </form>
        <select style={s.select} value={filters.category} onChange={(e) => setFilter('category', e.target.value)}>
          <option value="">{t('news.allCategories')}</option>
          {meta.categories.map((c) => <option key={c.name} value={c.name}>{c.name} ({c.count})</option>)}
        </select>
        <select style={s.select} value={filters.sentiment} onChange={(e) => setFilter('sentiment', e.target.value)}>
          <option value="">{t('news.allSentiments')}</option>
          {['bullish', 'bearish', 'neutral'].map((v) => <option key={v} value={v}>{t(`news.sentiment.${v}`)}</option>)}
        </select>
        <select style={s.select} value={filters.impact} onChange={(e) => setFilter('impact', e.target.value)}>
          <option value="">{t('news.allImpacts')}</option>
          {['high', 'medium', 'low'].map((v) => <option key={v} value={v}>{t(`news.impactLevel.${v}`)}</option>)}
        </select>
        <select style={s.select} value={filters.token} onChange={(e) => setFilter('token', e.target.value)}>
          <option value="">{t('news.allTokens')}</option>
          {meta.tokens.map((c) => <option key={c.name} value={c.name}>{c.name} ({c.count})</option>)}
        </select>
      </div>

      <div style={s.notice}>{t('news.disclaimer')}</div>

      {!ready ? (
        <div style={s.empty}>{t('news.loading')}</div>
      ) : error ? (
        <div style={s.empty}>{t('news.error')}</div>
      ) : data.docs.length === 0 ? (
        <div style={s.empty}>{t('news.empty')}</div>
      ) : (
        <>
          <div style={s.count}>{t('news.count', { count: data.total })}</div>
          <div style={s.list}>{data.docs.map((d) => <NewsCard key={d._id} doc={d} onToken={(tk) => setFilter('token', tk)} />)}</div>
          {pages > 1 && (
            <div style={s.pager}>
              <button style={s.pageBtn} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹</button>
              <span style={s.pageInfo}>{page} / {pages}</span>
              <button style={s.pageBtn} disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>›</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const s = {
  page: { padding: '24px 32px', maxWidth: 1400, margin: '0 auto' },
  header: { marginBottom: 14 },
  title: { margin: '0 0 4px', fontSize: 20, fontWeight: 800, color: 'var(--text)' },
  subtitle: { margin: 0, fontSize: 13, color: 'var(--text-muted)' },
  filters: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 },
  searchForm: { display: 'flex', gap: 6, flex: '1 1 260px' },
  input: { flex: 1, minWidth: 0, fontSize: 13, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)' },
  searchBtn: { fontSize: 12.5, fontWeight: 700, color: '#fff', background: 'var(--accent)', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer' },
  select: { fontSize: 12.5, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer' },
  notice: { fontSize: 11, color: 'var(--text-muted)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', marginBottom: 14 },
  empty: { color: 'var(--text-muted)', textAlign: 'center', padding: 60, fontSize: 14 },
  count: { fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 },
  list: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 10, alignItems: 'start' },
  card: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 6 },
  cardMeta: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, fontSize: 10.5 },
  sentiment: { fontWeight: 800, borderRadius: 999, padding: '1px 7px', fontSize: 10.5 },
  impact: { fontWeight: 700, fontSize: 10.5 },
  time: { marginLeft: 'auto', color: 'var(--text-dim)', fontSize: 10.5 },
  cardTitle: { margin: 0, fontSize: 13.5, fontWeight: 700, color: 'var(--text)', lineHeight: 1.35 },
  tokens: { display: 'flex', flexWrap: 'wrap', gap: 4 },
  token: { fontSize: 10.5, fontWeight: 800, background: 'transparent', border: '1px solid', borderRadius: 6, padding: '1px 6px', cursor: 'pointer' },
  summary: { margin: 0, fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 },
  clamp: { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
  analysis: { fontSize: 12, lineHeight: 1.55, color: 'var(--text)', background: 'var(--bg)', borderLeft: '3px solid var(--accent)', borderRadius: 6, padding: '6px 10px' },
  cardFoot: { display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' },
  tag: { fontSize: 10.5, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 999, padding: '1px 7px', color: 'var(--text-muted)' },
  sourceLink: { fontSize: 11, color: 'var(--accent)', textDecoration: 'none' },
  more: { fontSize: 11, color: 'var(--accent)', fontWeight: 700 },
  pager: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 16 },
  pageBtn: { fontSize: 16, fontWeight: 700, width: 34, height: 30, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer' },
  pageInfo: { fontSize: 12.5, color: 'var(--text-muted)' },
};
